import { useEffect, useState } from "react";
import { getArtistAlbums, Spotip17anFunctions } from "../../../../utils/api";
import SquareImage from "../../../shared/square-image/square-image";
import { ArtistDTO, PlaylistCreateDTO } from "../../spotipitan/types";
import ItemsHorizontalScroll from "../../../squadDiskShared/itemsHorizontalScroll/itemsHorizontalScroll";
import ItemDetailsToView from "../../../squadDiskShared/itemDetailsView/itemDetailsToView";
import './spotip17an-my-library.scss';
import ImageMosaic from "../../../shared/image-mosaic/image-mosaic";
import { useNavigate } from "react-router-dom";
import useUserStore from "../../../../store/UserState";
import CreatePlaylistModal from "../../../shared/create-playlist-modal/create-playlist-modal";
import { AlbumEditModal } from "../../../shared/album-edit-modal";
import { getPlaylistBdStatus } from "../../../../utils/dbUtils";
import { PaginatedResponse } from "../../../../utils/apiTypes";
import { isMobileDevice } from "../../../../utils/device-utils";

const Spotip17anMyLibrary = () => {

    const [playlists, setPlaylists] = useState<ArtistDTO[]>([]);
    const [subscriptions, setSubscriptions] = useState<ArtistDTO[]>([]);
    const [albums, setAlbums] = useState<ArtistDTO[]>([]);
    const [showCreateAlbumModal, setShowCreateAlbumModal] = useState(false);
    const { state: { user, spotip17anUser, offlineMode } } = useUserStore(state => state);
    const navigate = useNavigate();

    const onSetPlaylists = async (playlists: ArtistDTO[], method: (playlists: ArtistDTO[]) => void) => {

        const processedPlaylists: ArtistDTO[] = [];

        for (const playlist of playlists) {
            const processedPlaylist = { ...playlist };
            processedPlaylist.downloaded_status = await getPlaylistBdStatus(playlist.id);
            processedPlaylists.push(processedPlaylist);
        }

        method([...processedPlaylists]);
    }

    const handleCloseCreatePlaylist = () => {
        navigate('');
    }

    const handleSavePlaylist = (playlist: PlaylistCreateDTO) => {
        if (offlineMode) {
            return;
        }

        Spotip17anFunctions.use('createPlaylist').post(playlist).then((res: ArtistDTO) => {
            handleCloseCreatePlaylist();
            const favsPlaylist = playlists.find(x => x.id === "favs");
            if (favsPlaylist) {
                setPlaylists([favsPlaylist, res, ...playlists.filter(x => x.id !== "favs")]);
            } else {
                setPlaylists([res, ...playlists]);
            }
        }).catch(e => {
            console.error(e);
        })
    }

    useEffect(() => {
        if (!user?.uid || offlineMode) {
            return;
        }

        Spotip17anFunctions.use('myPlaylists').call({ max: 8 }).then((playlists: PaginatedResponse<ArtistDTO>) => onSetPlaylists(playlists.data, setPlaylists)).catch(() => {
        });
        Spotip17anFunctions.use('myPlaylistSubscriptions').call({ max: 8 }).then((playlists: PaginatedResponse<ArtistDTO>) => onSetPlaylists(playlists.data, setSubscriptions)).catch(() => {
        });

        if (spotip17anUser?.spotip17an_artist && (spotip17anUser?.id || user.uid)) {
            getArtistAlbums(spotip17anUser.id || user.uid).then((res) => {
                if (Array.isArray(res)) {
                    onSetPlaylists(res as any[], setAlbums);
                }
            }).catch((err) => {
                console.error("Error fetching artist albums:", err);
            });
        }
    }, [user, spotip17anUser, offlineMode]);

    const playlistCard = (artist: ArtistDTO, navigateUrl: string, includeDownloadStatus = true, showHover = true, size = 110, hideHoverOnPhone = false) => {
        return (
            <ItemDetailsToView key={artist.id}
                className={"spotip17an-my-library__horizontal-scroll-item"}
                description={(artist.songs_count ?? artist.songs?.length) + " canciones"}
                title={artist.username}
                onClick={() => navigate(navigateUrl)}
                showHover={showHover} iconClass=""
                disableHover={hideHoverOnPhone && !!isMobileDevice()}
                size={size}
                upperRight={includeDownloadStatus &&
                    <>
                        {artist.downloaded_status === 'downloading' &&
                            <i className="fa-solid fa-spinner fa-spin fa-lg"></i>}
                        {artist.downloaded_status === 'updated' &&
                            <i className="fa-solid fa-circle-down fa-lg"></i>}
                        {artist.downloaded_status === 'downloaded' &&
                            <i className="fa-solid fa-circle-check fa-lg"></i>}
                        {artist.downloaded_status === 'error' &&
                            <i className="fa-solid fa-circle-exclamation fa-lg"></i>}
                    </>}
                image={artist.poster_url || artist.songs?.length === 1 ? (
                    <SquareImage playlist={artist} song={artist.poster_url ? undefined : artist.songs?.[0]} style={{ width: size }} />
                ) : (
                    <ImageMosaic songs={artist.songs?.slice(0, 4) || []} />
                )}
            />
        )
    }

    return (
        <>
            <div className="spotip17an-my-library">
                {spotip17anUser?.spotip17an_artist && (
                    <div style={{ height: 'fit-content' }}>
                        <div className="container d-flex flex-row justify-content-between">
                            <div className="d-flex flex-row">
                                <div className="spotip17an-my-library__items-title" style={{ cursor: 'default' }}>
                                    Mis álbumes
                                </div>
                                <button
                                    onClick={() => setShowCreateAlbumModal(true)}
                                    className="spotip17an-my-library__items-title"
                                    title="Crear nuevo álbum"
                                >
                                    <i className="fa-solid fa-plus"></i>
                                </button>
                            </div>
                        </div>
                        {albums.length === 0 ? (
                            <div className="container text-muted small py-2">
                                Aún no has creado ningún álbum nativo. ¡Haz clic en el botón + para crear tu primer álbum!
                            </div>
                        ) : (
                            <ItemsHorizontalScroll className="container spotip17an-my-library__items-container">
                                {albums.map((album) => {
                                    return playlistCard(album, `/playlist/${album.id}`);
                                })}
                            </ItemsHorizontalScroll>
                        )}
                    </div>
                )}
                <div style={{ height: 'fit-content' }}>
                    <div className="container d-flex flex-row justify-content-between">
                        <div className="d-flex flex-row">
                            <div className="spotip17an-my-library__items-title" style={{
                                cursor: 'default',
                            }}>Mis playlists
                            </div>
                            <button onClick={() => {
                                navigate('#create-playlist');
                            }} className="spotip17an-my-library__items-title">
                                <i className="fa-solid fa-plus"></i>
                            </button>
                        </div>
                        <div>
                            <button onClick={() => {
                                navigate('/my-playlists');
                            }} className="spotip17an-my-library__items-title">
                                <i className="fa-solid fa-list"></i>
                            </button>
                        </div>
                    </div>
                    <ItemsHorizontalScroll className="container spotip17an-my-library__items-container">
                        {playlists.map((artist) => {
                            return playlistCard(artist, `/playlist/${artist.id}`);
                        })}
                    </ItemsHorizontalScroll>
                </div>
                <div style={{ height: 'fit-content' }}>
                    <div className="container d-flex flex-row justify-content-between">
                        <div className="spotip17an-my-library__items-title" style={{
                            cursor: 'default',
                        }}>Listas guardadas
                        </div>
                        <button onClick={() => {
                            navigate('/saved-playlists');
                        }} className="spotip17an-my-library__items-title">
                            <i className="fa-solid fa-list"></i>
                        </button>
                    </div>
                    <ItemsHorizontalScroll className="container spotip17an-my-library__items-container">
                        {subscriptions.map((artist) => {
                            return playlistCard(artist, `/playlist/${artist.id}`);
                        })}
                    </ItemsHorizontalScroll>
                </div>
            </div>
            <CreatePlaylistModal hash="create-playlist" onClose={handleCloseCreatePlaylist}
                onSave={handleSavePlaylist} />
            <AlbumEditModal
                show={showCreateAlbumModal}
                onClose={() => setShowCreateAlbumModal(false)}
                onSave={(newAlbum) => {
                    setAlbums(prev => [newAlbum as any, ...prev]);
                }}
            />
        </>
    );

}

export default Spotip17anMyLibrary;